import type { ElementStyle } from '../schema';

/**
 * Shared text accent effects — marker highlight + long shadow — used by BOTH
 * the admin editor and the playback renderers so authored accents look
 * identical everywhere. The highlight geometry lives in CSS (see `player.css`
 * `[data-text-hl]` rules, mirrored in the admin's globals.css): each rendered
 * line gets a bar that extends 10px past the text on the left/right/bottom
 * while its top edge sits 10px BELOW the line top, so letters poke out above
 * the bar (sticker-label look).
 */

/** Long-shadow extrusion length (px). Authored `longShadowLength` wins; else
 *  scaled to the font size. */
export function resolveLongShadowLength(
  style: ElementStyle | undefined,
  fontSize: number,
): number {
  const authored = style?.longShadowLength;
  if (typeof authored === 'number' && authored > 0) return Math.min(120, Math.round(authored));
  return Math.max(4, Math.min(48, Math.round(fontSize * 0.4)));
}

/** Flat 45° "long shadow" for HTML text: a chain of 1px-offset text-shadows. */
export function longShadowCss(color: string, length: number): string {
  const parts: string[] = [];
  for (let i = 1; i <= length; i++) parts.push(`${i}px ${i}px 0 ${color}`);
  return parts.join(', ');
}

/**
 * Long shadow for SVG content (the chalk-glyph handwriting), where text-shadow
 * doesn't apply: a doubling chain of drop-shadows. Chained drop-shadows
 * compound (each applies to the previous result), so offsets 1,2,4,8… fill the
 * whole diagonal densely with ~log2(length) filters.
 */
export function longShadowFilterCss(color: string, length: number): string {
  const parts: string[] = [];
  let total = 0;
  for (let step = 1; total + step <= length; step *= 2) {
    parts.push(`drop-shadow(${step}px ${step}px 0 ${color})`);
    total += step;
  }
  if (parts.length === 0) parts.push(`drop-shadow(1px 1px 0 ${color})`);
  return parts.join(' ');
}

export interface TextEffectAttrs {
  style: React.CSSProperties;
  'data-text-hl'?: '';
}

/**
 * Attributes to spread onto an HTML text container to apply the authored
 * accent effects. Returns an empty `style` when the element has none.
 */
export function textEffectAttrs(
  style: ElementStyle | undefined,
  fontSize: number,
): TextEffectAttrs {
  const out: TextEffectAttrs = { style: {} };
  const hl = style?.highlightColor;
  if (typeof hl === 'string' && hl.trim()) {
    out['data-text-hl'] = '';
    (out.style as Record<string, unknown>)['--text-hl'] = hl;
  }
  const ls = style?.longShadowColor;
  if (typeof ls === 'string' && ls.trim()) {
    out.style.textShadow = longShadowCss(ls, resolveLongShadowLength(style, fontSize));
  }
  return out;
}

/**
 * Accent attrs for the SVG handwriting container (chalk-glyph path): highlight
 * becomes a block box behind the writing; long shadow becomes a drop-shadow
 * filter chain. Null when the element has no accents (skip the wrapper).
 */
export function textEffectSvgAttrs(
  style: ElementStyle | undefined,
  fontSize: number,
): TextEffectAttrs | null {
  const out: TextEffectAttrs = { style: {} };
  let any = false;
  const hl = style?.highlightColor;
  if (typeof hl === 'string' && hl.trim()) {
    out['data-text-hl'] = '';
    (out.style as Record<string, unknown>)['--text-hl'] = hl;
    any = true;
  }
  const ls = style?.longShadowColor;
  if (typeof ls === 'string' && ls.trim()) {
    out.style.filter = longShadowFilterCss(ls, resolveLongShadowLength(style, fontSize));
    any = true;
  }
  return any ? out : null;
}
